/**
 * weather_fx — the PURE recipes behind the weather particles (2026-09-11,
 * "make the weather more realistic"). Node-tested by verify:weather:resolver.
 *
 * RAIN falls at terminal velocity — Gunn & Kinzer (1949): 1 mm drops ≈ 4 m/s,
 * 2 mm ≈ 6.5, 3 mm ≈ 8, 5 mm ≈ 9 — so the drops SPAWN at speed and gravity is
 * off (the first cut accelerated at 4.5 g and crossed the eye line at 70 m/s).
 * A drop reads as a STREAK (Garg & Nayar 2006): a vertical streak sprite on a
 * camera-facing quad. Heavier rain: bigger drops and more of them
 * (Marshall–Palmer).
 * SNOW falls at 1–2 m/s and rides every eddy: curl noise, a wide size range,
 * a long life; heavy snow is a whiteout (the resolver's fog term).
 * DUST (a sandstorm) streams PAST the camera along the wind at 6–14 m/s in a
 * box spawned UPWIND, a tan sprite that grows and fades, with big faint
 * far sheets behind it.
 * Every config keeps rate × lifetime ≤ maxParticles (the pool law).
 */

/** three's NormalBlending (this lib stays three-free for the harness). */
export const NORMAL_BLENDING = 1;

export interface Curve { time: number; value: number }
export interface FxConfig {
  /** Pool capacity — ≥ rate × the longest life × the density ceiling. */
  maxParticles: number;
  lifetime: number | { min: number; max: number };
  startSpeed: number | { min: number; max: number };
  startSize: number | { min: number; max: number };
  startColor: number;
  simulationSpace: 'world';
  shape: { type: 'box'; halfExtents: { x: number; y: number; z: number } };
  emission: { rateOverTime: number };
  blending: number;
  depthWrite: boolean;
  forces: { gravityModifier: number; drag: number; curl?: { strength: number; frequency: number; scrollSpeed: number } };
  /** The CPU adapter's turbulence (it ignores forces.curl) — the module mirrors curl into it. */
  noise?: { strength: number; frequency: number; scrollSpeed: number };
  sizeOverLifetime?: Curve[];
  alphaOverLifetime?: Curve[];
  /** Set by the module: a streak sprite — 'fall' (a vertical rain streak) or 'wind' (a horizontal sand streak). */
  streak?: 'fall' | 'wind';
}

// The precipitation volumes: a thin NEAR slab (the "inside the storm" streaks)
// and a wide MID slab (distant sheets), both above the camera; the ground
// splash slab; the dust boxes spawned UPWIND so the storm streams past.
const NEAR_BOX = { x: 45, y: 2, z: 45 };
const MID_BOX = { x: 190, y: 6, z: 190 };
const SPLASH_BOX = { x: 16, y: 0.15, z: 16 };
const DUST_NEAR_BOX = { x: 26, y: 9, z: 26 };
const DUST_FAR_BOX = { x: 110, y: 28, z: 110 };
/** The SALTATION layer: sand hops within a metre of the ground (Bagnold) — a thin fast box at knee height. */
const DUST_SALT_BOX = { x: 30, y: 0.9, z: 30 };

/** Rate factors per layer (× density × the tier's particleCap). */
export const RATE = { rainNear: 5200, rainMid: 1500, splash: 2000, snowNear: 1900, snowMid: 650, dustNear: 900, dustFar: 240, dustSalt: 1600 } as const;

export const RAIN_NEAR: FxConfig = {
  maxParticles: 14000, lifetime: { min: 2.2, max: 2.7 }, startSpeed: { min: -12.5, max: -9.5 }, startSize: { min: 0.55, max: 0.95 },
  startColor: 0xc2d8ea, simulationSpace: 'world', shape: { type: 'box', halfExtents: NEAR_BOX }, emission: { rateOverTime: RATE.rainNear },
  blending: NORMAL_BLENDING, depthWrite: false, forces: { gravityModifier: 0, drag: 0 }, streak: 'fall',
  alphaOverLifetime: [{ time: 0, value: 0.5 }, { time: 0.85, value: 0.5 }, { time: 1, value: 0 }],
};
export const RAIN_MID: FxConfig = {
  maxParticles: 7000, lifetime: { min: 4.2, max: 4.8 }, startSpeed: { min: -12, max: -10 }, startSize: { min: 0.22, max: 0.36 },
  startColor: 0x86a0bd, simulationSpace: 'world', shape: { type: 'box', halfExtents: MID_BOX }, emission: { rateOverTime: RATE.rainMid },
  blending: NORMAL_BLENDING, depthWrite: false, forces: { gravityModifier: 0, drag: 0 },
  alphaOverLifetime: [{ time: 0, value: 0.3 }, { time: 0.9, value: 0.3 }, { time: 1, value: 0 }],
};
export const RAIN_SPLASH: FxConfig = {
  maxParticles: 2500, lifetime: 0.3, startSpeed: 1.6, startSize: 0.035, startColor: 0xcfe0ee, simulationSpace: 'world',
  shape: { type: 'box', halfExtents: SPLASH_BOX }, emission: { rateOverTime: RATE.splash }, blending: NORMAL_BLENDING, depthWrite: false,
  forces: { gravityModifier: 2.2, drag: 0.05 },
};
export const SNOW_NEAR: FxConfig = {
  maxParticles: 22000, lifetime: { min: 9, max: 12 }, startSpeed: { min: -1.9, max: -1.1 }, startSize: { min: 0.06, max: 0.2 },
  startColor: 0xf4f8ff, simulationSpace: 'world', shape: { type: 'box', halfExtents: NEAR_BOX }, emission: { rateOverTime: RATE.snowNear },
  blending: NORMAL_BLENDING, depthWrite: false, forces: { gravityModifier: 0.05, drag: 0.6, curl: { strength: 0.9, frequency: 0.45, scrollSpeed: 0.3 } },
  alphaOverLifetime: [{ time: 0, value: 0.9 }, { time: 0.9, value: 0.9 }, { time: 1, value: 0 }],
};
export const SNOW_MID: FxConfig = {
  maxParticles: 14000, lifetime: { min: 17, max: 22 }, startSpeed: { min: -1.9, max: -1.2 }, startSize: { min: 0.35, max: 0.6 },
  startColor: 0xe8eef8, simulationSpace: 'world', shape: { type: 'box', halfExtents: MID_BOX }, emission: { rateOverTime: RATE.snowMid },
  blending: NORMAL_BLENDING, depthWrite: false, forces: { gravityModifier: 0.05, drag: 0.6, curl: { strength: 0.6, frequency: 0.3, scrollSpeed: 0.2 } },
  alphaOverLifetime: [{ time: 0, value: 0.5 }, { time: 0.9, value: 0.5 }, { time: 1, value: 0 }],
};
export const DUST_NEAR: FxConfig = {
  maxParticles: 4500, lifetime: { min: 3, max: 5 }, startSpeed: { min: 7, max: 12 }, startSize: { min: 0.5, max: 1.4 },
  startColor: 0xb8915c, simulationSpace: 'world', shape: { type: 'box', halfExtents: DUST_NEAR_BOX }, emission: { rateOverTime: RATE.dustNear },
  blending: NORMAL_BLENDING, depthWrite: false, forces: { gravityModifier: 0.02, drag: 0.12, curl: { strength: 2.2, frequency: 0.25, scrollSpeed: 0.6 } }, streak: 'wind',
  sizeOverLifetime: [{ time: 0, value: 0.8 }, { time: 1, value: 1.5 }],
  alphaOverLifetime: [{ time: 0, value: 0 }, { time: 0.2, value: 0.24 }, { time: 0.7, value: 0.2 }, { time: 1, value: 0 }],
};
export const DUST_FAR: FxConfig = {
  maxParticles: 2600, lifetime: { min: 7, max: 11 }, startSpeed: { min: 8, max: 14 }, startSize: { min: 2.5, max: 6 },
  startColor: 0xb9955f, simulationSpace: 'world', shape: { type: 'box', halfExtents: DUST_FAR_BOX }, emission: { rateOverTime: RATE.dustFar },
  blending: NORMAL_BLENDING, depthWrite: false, forces: { gravityModifier: 0, drag: 0.08, curl: { strength: 1.5, frequency: 0.12, scrollSpeed: 0.3 } }, streak: 'wind',
  sizeOverLifetime: [{ time: 0, value: 0.8 }, { time: 1, value: 1.6 }],
  alphaOverLifetime: [{ time: 0, value: 0 }, { time: 0.25, value: 0.14 }, { time: 1, value: 0 }],
};
/** SALTATION: the sand that actually comes off the ground — small fast grains hopping within a metre of it, streaking along the wind. */
export const DUST_SALT: FxConfig = {
  maxParticles: 3600, lifetime: { min: 1.2, max: 2.2 }, startSpeed: { min: 10, max: 16 }, startSize: { min: 0.08, max: 0.2 },
  startColor: 0xc7a36e, simulationSpace: 'world', shape: { type: 'box', halfExtents: DUST_SALT_BOX }, emission: { rateOverTime: RATE.dustSalt },
  blending: NORMAL_BLENDING, depthWrite: false, forces: { gravityModifier: 0.15, drag: 0.1, curl: { strength: 1.2, frequency: 0.6, scrollSpeed: 0.8 } }, streak: 'wind',
  alphaOverLifetime: [{ time: 0, value: 0 }, { time: 0.15, value: 0.5 }, { time: 0.8, value: 0.4 }, { time: 1, value: 0 }],
};

/** Where each volume sits relative to the camera (m up) and, for dust, UPWIND (m). */
export const VOLUME = {
  rain: { near: 14, mid: 30 },
  snow: { near: 9, mid: 20 },
  dust: { nearUp: 5, nearUpwind: 22, farUp: 12, farUpwind: 70, saltUp: -1.0, saltUpwind: 18 },
  splashDown: 1.55,
} as const;

/** The pool law: rate × the longest life must fit the pool (× the density ceiling 0.9 the module applies). */
export function poolFits(cfg: FxConfig, rateFactor: number, densityMax = 0.95): boolean {
  const life = typeof cfg.lifetime === 'number' ? cfg.lifetime : cfg.lifetime.max;
  return rateFactor * densityMax * life <= cfg.maxParticles;
}

/** Emission rate for a layer: intensity^1.3 (light rain is sparse, heavy rain is a wall — Marshall–Palmer's N grows faster than linearly). */
export function emissionRate(kind: 'rain' | 'snow' | 'dust', layer: 'near' | 'mid' | 'splash' | 'salt', density: number, cap: number): number {
  const d = Math.min(0.95, Math.max(0, density));
  const shaped = kind === 'dust' ? d : Math.pow(d, 1.3);
  const f = kind === 'rain' ? (layer === 'near' ? RATE.rainNear : layer === 'mid' ? RATE.rainMid : RATE.splash)
    : kind === 'snow' ? (layer === 'near' ? RATE.snowNear : RATE.snowMid)
    : (layer === 'near' ? RATE.dustNear : layer === 'salt' ? RATE.dustSalt : RATE.dustFar);
  return shaped * f * Math.max(0, cap);
}
